/**
 * Outreach Service - Firebase operations for Outreach Campaigns
 * 
 * This service handles all Firebase CRUD operations for the Outreach feature,
 * including campaigns, sender connections, and candidate tracking.
 */

import { database, auth } from '../firebase';
import { ref, get, set, update, remove, onValue, push, serverTimestamp } from 'firebase/database';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get the current authenticated user's company ID
 * Supports both admin companies and collaborators
 */
export const getCompanyId = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    // Check if user is a collaborator first
    const collaboratorRef = ref(database, `collaborators/${user.uid}`);
    const collaboratorSnap = await get(collaboratorRef);

    if (collaboratorSnap.exists()) {
        const collaboratorData = collaboratorSnap.val();
        return collaboratorData.companyId || collaboratorData.company_uid;
    }

    // Otherwise, user is the admin/owner
    return user.uid;
};

/**
 * Create a safe Firebase key from any string
 */
const safeFirebaseKey = (key) => {
    return key?.replace(/[.#$[\]]/g, '_') || '';
};

// ============================================================================
// CAMPAIGN CRUD OPERATIONS
// ============================================================================

/**
 * Create a new outreach campaign
 * @param {string} companyId - Company UID
 * @param {object} campaignData - Campaign data
 * @returns {object} Created campaign with ID
 */
export const createCampaign = async (companyId, campaignData) => {
    const campaignsRef = ref(database, `companies/${companyId}/outreach_campaigns`);
    const newCampaignRef = push(campaignsRef);

    const campaign = {
        ...campaignData,
        id: newCampaignRef.key,
        status: 'draft',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        publishedAt: null,
        nodes: [],
        stats: {
            total: 0,
            completed: 0,
            active: 0,
            replied: 0
        }
    };

    await set(newCampaignRef, campaign);
    return campaign;
};

/**
 * Get all campaigns for a company
 * @param {string} companyId - Company UID
 * @returns {array} List of campaigns
 */
export const getCampaigns = async (companyId) => {
    const campaignsRef = ref(database, `companies/${companyId}/outreach_campaigns`);
    const snapshot = await get(campaignsRef);

    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    return Object.keys(data).map(key => ({
        id: key,
        ...data[key]
    })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
};

/**
 * Subscribe to campaigns with real-time updates
 * @param {string} companyId - Company UID
 * @param {function} callback - Callback function with campaigns array
 * @returns {function} Unsubscribe function
 */
export const subscribeToCampaigns = (companyId, callback) => {
    const campaignsRef = ref(database, `companies/${companyId}/outreach_campaigns`);

    return onValue(campaignsRef, (snapshot) => {
        if (!snapshot.exists()) {
            callback([]);
            return;
        }

        const data = snapshot.val();
        const campaigns = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
        })).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

        callback(campaigns);
    });
};

/**
 * Get a single campaign by ID
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 * @returns {object|null} Campaign data or null
 */
export const getCampaign = async (companyId, campaignId) => {
    const campaignRef = ref(database, `companies/${companyId}/outreach_campaigns/${campaignId}`);
    const snapshot = await get(campaignRef);

    if (!snapshot.exists()) return null;
    return { id: campaignId, ...snapshot.val() };
};

/**
 * Subscribe to a single campaign with real-time updates
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 * @param {function} callback - Callback function
 * @returns {function} Unsubscribe function
 */
export const subscribeToCampaign = (companyId, campaignId, callback) => {
    const campaignRef = ref(database, `companies/${companyId}/outreach_campaigns/${campaignId}`);

    return onValue(campaignRef, (snapshot) => {
        if (!snapshot.exists()) {
            callback(null);
            return;
        }
        callback({ id: campaignId, ...snapshot.val() });
    });
};

/**
 * Update a campaign
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 * @param {object} updates - Fields to update
 */
export const updateCampaign = async (companyId, campaignId, updates) => {
    const campaignRef = ref(database, `companies/${companyId}/outreach_campaigns/${campaignId}`);
    await update(campaignRef, {
        ...updates,
        updatedAt: Date.now()
    });
};

/**
 * Delete a campaign
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 */
export const deleteCampaign = async (companyId, campaignId) => {
    const campaignRef = ref(database, `companies/${companyId}/outreach_campaigns/${campaignId}`);
    await remove(campaignRef);
};

// ============================================================================
// WORKFLOW NODE OPERATIONS
// ============================================================================

/**
 * Save workflow nodes for a campaign
 * Nodes include email steps and timer steps
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 * @param {array} nodes - Array of node objects
 */
export const saveWorkflowNodes = async (companyId, campaignId, nodes) => {
    // Format nodes for easy backend processing
    const formattedNodes = nodes.map((node, index) => ({
        ...node,
        order: index,
        // For email nodes, ensure required fields
        ...(node.type === 'email' && {
            subject: node.subject || '',
            body: node.body || '',
            from: node.from || '',
            cc: node.cc || '',
            bcc: node.bcc || ''
        }),
        // For timer nodes, store duration as days (integer)
        ...(node.type === 'timer' && {
            durationDays: parseInt(node.duration) || 2
        })
    }));

    // Extract the primary sender email from the first email node found
    const firstEmailNode = nodes.find(n => n.type === 'email' && n.from);
    const senderEmail = firstEmailNode ? firstEmailNode.from : null;

    const updates = { nodes: formattedNodes };
    if (senderEmail) {
        updates.senderEmail = senderEmail;
    }

    await updateCampaign(companyId, campaignId, updates);
};

/**
 * Update a single node in the workflow
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 * @param {number} nodeIndex - Index of the node
 * @param {object} nodeData - Updated node data
 */
export const updateWorkflowNode = async (companyId, campaignId, nodeIndex, nodeData) => {
    const nodeRef = ref(database, `companies/${companyId}/outreach_campaigns/${campaignId}/nodes/${nodeIndex}`);
    await update(nodeRef, nodeData);

    // Update campaign timestamp
    const campaignRef = ref(database, `companies/${companyId}/outreach_campaigns/${campaignId}`);
    await update(campaignRef, { updatedAt: Date.now() });
};

// ============================================================================
// SHORTLIST OPERATIONS
// ============================================================================

/**
 * Get all shortlists for a company
 * @param {string} companyId - Company UID
 * @returns {array} List of shortlists with name and count
 */
export const getShortlists = async (companyId) => {
    const shortlistsRef = ref(database, `companies/${companyId}/tal_shortlists`);
    const snapshot = await get(shortlistsRef);

    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    return Object.keys(data).map(name => {
        const profiles = data[name]?.profiles || {};
        return {
            name: name,
            profilesCount: Object.keys(profiles).length,
            createdAt: data[name]?.createdAt || 0
        };
    });
};

/**
 * Get all profiles in a shortlist with required fields for email drafting
 * @param {string} companyId - Company UID
 * @param {string} shortlistName - Shortlist name
 * @returns {array} List of profile objects
 */
export const getShortlistProfiles = async (companyId, shortlistName) => {
    const profilesRef = ref(database, `companies/${companyId}/tal_shortlists/${shortlistName}/profiles`);
    const snapshot = await get(profilesRef);

    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    return Object.keys(data).map(key => {
        const profile = data[key];

        // --- HELPER LOGIC TO CLEAN UP "JOBSEEQR" LEGACY DATA ---
        let cleanCompany = profile.company || '';
        let cleanTitle = profile.jobTitle || '';

        // If company is missing OR it looks like a long description (> 50 chars), try to extract from other fields
        // The screenshot showed "JOBSEEQR is Lebanon's first..." which is definitely > 50 chars
        if (!cleanCompany || cleanCompany.length > 50 || cleanCompany === profile.company_info) {
            // Try to parse from experience string?
            // Common format seen: "Role, Company, Date" -> "Founder & CEO, JOBSEEQR, Oct 2023..."
            if (profile.experience && typeof profile.experience === 'string') {
                const parts = profile.experience.split(',');
                if (parts.length >= 2) {
                    // Heuristic: If part[1] is short, likely the company
                    const candidateComp = parts[1].trim();
                    if (candidateComp.length < 50) {
                        cleanCompany = candidateComp;
                        // If we extracted company from exp, maybe part[0] is the clean title?
                        if (!cleanTitle || cleanTitle.length > 50) {
                            cleanTitle = parts[0].trim();
                        }
                    }
                }
            }

            // If still bad, check company_info length
            if ((!cleanCompany || cleanCompany.length > 50) && profile.company_info && profile.company_info.length < 50) {
                cleanCompany = profile.company_info;
            }

            // Absolute fallback
            if (!cleanCompany && profile.experience && profile.experience.includes(' at ')) {
                const atParts = profile.experience.split(' at ');
                if (atParts.length > 1) {
                    cleanCompany = atParts[1].split(/[,\-]/)[0].trim(); // Take first part after 'at'
                }
            }
        }

        // Clean up title if it contains the full string
        if (cleanTitle && cleanTitle.length > 60 && cleanTitle.includes(',')) {
            const titleParts = cleanTitle.split(',');
            if (titleParts.length > 0) cleanTitle = titleParts[0].trim();
        }

        return {
            id: key,
            user_uid: profile.user_uid || key,
            pinecone_id: profile.pinecone_id || profile.id || null, // Important for analysis lookup
            firstName: profile.firstName || profile.username?.split(' ')[0] || '',
            lastName: profile.lastName || profile.username?.split(' ').slice(1).join(' ') || '',
            fullName: profile.fullUsername || profile.username || `${profile.firstName || ''} ${profile.lastName || ''}`.trim(),
            email: profile.email || null,
            phoneNumber: profile.phoneNumber || null,
            linkedin: profile.linkedin || profile.linkedInURL || '',
            // Improved company fallback
            company: cleanCompany || '',
            // Improved job title fallback
            jobTitle: cleanTitle || '',
            profileSpecificContext: profile.profileSpecificContext || profile.key_achievements || '',
            // Pass through additional metadata
            location: profile.location || '',
            skills: profile.skills || [],
            tags: profile.tags || [],
            full_cv: profile.full_cv || null,
            experience: profile.experience || '',
            education: profile.education || '',
            openToWork: profile.openToWork || false
        };
    });
};

/**
 * Remove a profile from a shortlist
 * @param {string} companyId - Company UID
 * @param {string} shortlistName - Shortlist name
 * @param {string} profileId - Profile ID to remove
 */
export const removeProfileFromShortlist = async (companyId, shortlistName, profileId) => {
    const profileRef = ref(database, `companies/${companyId}/tal_shortlists/${shortlistName}/profiles/${profileId}`);
    await remove(profileRef);
};

/**
 * Subscribe to shortlist profiles with real-time updates
 * @param {string} companyId - Company UID
 * @param {string} shortlistName - Shortlist name
 * @param {function} callback - Callback function
 * @returns {function} Unsubscribe function
 */
export const subscribeToShortlistProfiles = (companyId, shortlistName, callback) => {
    const profilesRef = ref(database, `companies/${companyId}/tal_shortlists/${shortlistName}/profiles`);

    return onValue(profilesRef, (snapshot) => {
        if (!snapshot.exists()) {
            callback([]);
            return;
        }

        const data = snapshot.val();
        const profiles = Object.keys(data).map(key => {
            const profile = data[key];
            return {
                id: key,
                user_uid: profile.user_uid || key,
                firstName: profile.firstName || profile.username?.split(' ')[0] || '',
                lastName: profile.lastName || profile.username?.split(' ').slice(1).join(' ') || '',
                fullName: profile.fullUsername || profile.username || '',
                email: profile.email || null,
                company: profile.company || '',
                jobTitle: profile.jobTitle || ''
            };
        });

        callback(profiles);
    });
};

// ============================================================================
// CONNECTED SENDER OPERATIONS
// ============================================================================

/**
 * Get all connected sender emails for a company
 * @param {string} companyId - Company UID
 * @returns {array} List of connected sender objects
 */
export const getConnectedSenders = async (companyId) => {
    const sendersRef = ref(database, `companies/${companyId}/connected_senders`);
    const snapshot = await get(sendersRef);

    if (!snapshot.exists()) return [];

    const data = snapshot.val();
    return Object.keys(data).map(key => ({
        id: key,
        email: data[key].email,
        provider: data[key].provider,
        connectedAt: data[key].connectedAt,
        isActive: data[key].isActive !== false
    }));
};

/**
 * Subscribe to connected senders with real-time updates
 * @param {string} companyId - Company UID
 * @param {function} callback - Callback function
 * @returns {function} Unsubscribe function
 */
export const subscribeToConnectedSenders = (companyId, callback) => {
    const sendersRef = ref(database, `companies/${companyId}/connected_senders`);

    return onValue(sendersRef, (snapshot) => {
        if (!snapshot.exists()) {
            callback([]);
            return;
        }

        const data = snapshot.val();
        const senders = Object.keys(data).map(key => ({
            id: key,
            email: data[key].email,
            provider: data[key].provider,
            connectedAt: data[key].connectedAt,
            isActive: data[key].isActive !== false
        }));

        callback(senders);
    });
};

// ============================================================================
// CANDIDATE STATUS OPERATIONS
// ============================================================================

/**
 * Get candidate statuses for a campaign
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 * @returns {object} Map of candidateId -> status object
 */
export const getCandidateStatuses = async (companyId, campaignId) => {
    const statusRef = ref(database, `companies/${companyId}/outreach_campaigns/${campaignId}/candidate_status`);
    const snapshot = await get(statusRef);

    if (!snapshot.exists()) return {};
    return snapshot.val();
};

/**
 * Subscribe to candidate statuses with real-time updates
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 * @param {function} callback - Callback function
 * @returns {function} Unsubscribe function
 */
export const subscribeToCandidateStatuses = (companyId, campaignId, callback) => {
    const statusRef = ref(database, `companies/${companyId}/outreach_campaigns/${campaignId}/candidate_status`);

    return onValue(statusRef, (snapshot) => {
        callback(snapshot.exists() ? snapshot.val() : {});
    });
};

// ============================================================================
// CAMPAIGN ACTIONS (API CALLS)
// ============================================================================

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL_PROD;

/**
 * Publish a campaign (triggers email scheduling)
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 * @returns {object} Response from backend
 */
export const publishCampaign = async (companyId, campaignId) => {
    const response = await fetch(`${API_BASE_URL}/outreach/publish`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ company_uid: companyId, campaign_id: campaignId })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to publish campaign');
    }

    return response.json();
};

/**
 * Pause an active campaign
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 */
export const pauseCampaign = async (companyId, campaignId) => {
    const response = await fetch(`${API_BASE_URL}/outreach/pause`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ company_uid: companyId, campaign_id: campaignId })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to pause campaign');
    }

    return response.json();
};

/**
 * Resume a paused campaign
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 */
export const resumeCampaign = async (companyId, campaignId) => {
    const response = await fetch(`${API_BASE_URL}/outreach/resume`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ company_uid: companyId, campaign_id: campaignId })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to resume campaign');
    }

    return response.json();
};

/**
 * Update a live campaign (updates scheduled emails without changing dates)
 * @param {string} companyId - Company UID
 * @param {string} campaignId - Campaign ID
 * @param {object} updates - Updates to apply
 */
export const updateLiveCampaign = async (companyId, campaignId, updates) => {
    const response = await fetch(`${API_BASE_URL}/outreach/update`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            company_uid: companyId,
            campaign_id: campaignId,
            updates
        })
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update campaign');
    }

    return response.json();
};

// ============================================================================
// SENDER CONNECTION (API CALLS)
// ============================================================================

/**
 * Get Gmail OAuth URL for connecting sender email
 * @param {string} companyId - Company UID
 * @returns {string} OAuth authorization URL
 */
export const getGmailAuthUrl = async (companyId) => {
    const response = await fetch(`${API_BASE_URL}/outreach/sender/connect?company_uid=${companyId}&provider=google`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    if (!response.ok) throw new Error('Failed to get Gmail auth URL');

    const data = await response.json();
    return data.auth_url;
};

/**
 * Get Outlook OAuth URL for connecting sender email
 * @param {string} companyId - Company UID
 * @returns {string} OAuth authorization URL
 */
export const getOutlookAuthUrl = async (companyId) => {
    const response = await fetch(`${API_BASE_URL}/outreach/sender/connect?company_uid=${companyId}&provider=microsoft`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        }
    });

    if (!response.ok) throw new Error('Failed to get Outlook auth URL');

    const data = await response.json();
    return data.auth_url;
};

/**
 * Disconnect a sender email
 * @param {string} companyId - Company UID
 * @param {string} senderId - Sender ID to disconnect
 */
export const disconnectSender = async (companyId, senderId) => {
    const response = await fetch(`${API_BASE_URL}/outreach/sender/disconnect`, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ company_uid: companyId, sender_id: senderId })
    });

    if (!response.ok) throw new Error('Failed to disconnect sender');

    return response.json();
};

export default {
    // Auth helpers
    getCompanyId,

    // Campaign CRUD
    createCampaign,
    getCampaigns,
    getCampaign,
    updateCampaign,
    deleteCampaign,
    subscribeToCampaigns,
    subscribeToCampaign,

    // Workflow
    saveWorkflowNodes,
    updateWorkflowNode,

    // Shortlists
    getShortlists,
    getShortlistProfiles,
    removeProfileFromShortlist,
    subscribeToShortlistProfiles,

    // Senders
    getConnectedSenders,
    subscribeToConnectedSenders,

    // Candidate status
    getCandidateStatuses,
    subscribeToCandidateStatuses,

    // Campaign actions
    publishCampaign,
    pauseCampaign,
    resumeCampaign,
    updateLiveCampaign,

    // Sender connection
    getGmailAuthUrl,
    getOutlookAuthUrl,
    disconnectSender
};
